/**
 * Sinhala / Tamil renderings of the demo-fixture strings in `mock_data.ts`.
 *
 * Why these are NOT in `packages/jumpstone-i18n`
 * -------------------------------------------
 * The shared catalogue holds *product copy* — strings the product itself owns,
 * which must exist in all five locales and are enforced by the i18n CI checks.
 * "Engineering", "Kandy Branch" and "Employment contract" are neither: in
 * production they are **tenant data**, returned by the API exactly as the
 * customer typed it. Putting them in the catalogue would assert that the
 * product owns a canonical translation of every customer's department names,
 * and would oblige an Indonesian and French translation of a Sri Lankan demo
 * company's org chart.
 *
 * They still need to render natively, because a Sinhala or Tamil evaluator
 * looking at the demo should not see an English-only employee directory. So the
 * demo layer — and only the demo layer — carries its own lookup. When these
 * screens are wired to the real API, this file is deleted along with
 * `mock_data.ts`; no product code depends on it.
 *
 * Person names, bank names, company names and asset models are deliberately
 * absent: those are proper nouns and are never translated.
 */

/** Sinhala + Tamil for one fixture string. */
interface FixtureLabel {
  si: string;
  ta: string;
}

/** Keyed by the exact English fixture value used in `mock_data.ts`. */
export const MOCK_FIXTURE_LABELS: Record<string, FixtureLabel> = {
  // ── Departments ───────────────────────────────────────────────────────────
  Engineering: { si: 'ඉංජිනේරු', ta: 'பொறியியல்' },
  Finance: { si: 'මූල්‍ය', ta: 'நிதி' },
  Operations: { si: 'මෙහෙයුම්', ta: 'செயற்பாடுகள்' },
  Sales: { si: 'විකුණුම්', ta: 'விற்பனை' },
  HR: { si: 'මානව සම්පත්', ta: 'மனித வளம்' },

  // ── Job titles ────────────────────────────────────────────────────────────
  'Software Engineer': {
    si: 'මෘදුකාංග ඉංජිනේරු',
    ta: 'மென்பொருள் பொறியியலாளர்',
  },
  'QA Engineer': {
    si: 'තත්ත්ව සහතික ඉංජිනේරු',
    ta: 'தர உறுதிப் பொறியியலாளர்',
  },
  'Finance Manager': { si: 'මූල්‍ය කළමනාකරු', ta: 'நிதி மேலாளர்' },
  Accountant: { si: 'ගණකාධිකාරී', ta: 'கணக்காளர்' },
  'HR Business Partner': {
    si: 'මානව සම්පත් ව්‍යාපාරික සහයෝගී',
    ta: 'மனித வள வணிகப் பங்காளர்',
  },
  'Account Executive': { si: 'ගිණුම් විධායක', ta: 'கணக்கு நிர்வாகி' },
  'HR Executive': { si: 'මානව සම්පත් විධායක', ta: 'மனித வள நிர்வாகி' },
  // Historical titles, reachable via employment/salary history and movements.
  'Senior Software Engineer': {
    si: 'ජ්‍යෙෂ්ඨ මෘදුකාංග ඉංජිනේරු',
    ta: 'முதுநிலை மென்பொருள் பொறியாளர்',
  },
  'Senior Accountant': {
    si: 'ජ්‍යෙෂ්ඨ ගණකාධිකාරී',
    ta: 'முதுநிலை கணக்காளர்',
  },
  'Operations Lead': { si: 'මෙහෙයුම් ප්‍රධානී', ta: 'செயற்பாட்டுத் தலைவர்' },
  'Warehouse Supervisor': {
    si: 'ගබඩා අධීක්ෂක',
    ta: 'பண்டகசாலை மேற்பார்வையாளர்',
  },

  // ── Org-chart titles ──────────────────────────────────────────────────────
  CEO: { si: 'ප්‍රධාන විධායක නිලධාරී', ta: 'தலைமை நிர்வாக அதிகாரி' },
  'VP Engineering': {
    si: 'ඉංජිනේරු උපසභාපති',
    ta: 'பொறியியல் துணைத் தலைவர்',
  },
  'Sales Director': { si: 'විකුණුම් අධ්‍යක්ෂ', ta: 'விற்பனை இயக்குநர்' },

  // ── Locations and units ───────────────────────────────────────────────────
  'Colombo HQ': {
    si: 'කොළඹ ප්‍රධාන කාර්යාලය',
    ta: 'கொழும்பு தலைமையகம்',
  },
  'Colombo 03': { si: 'කොළඹ 03', ta: 'கொழும்பு 03' },
  'Kandy Branch': { si: 'මහනුවර ශාඛාව', ta: 'கண்டி கிளை' },
  Kandy: { si: 'මහනුවර', ta: 'கண்டி' },
  Galle: { si: 'ගාල්ල', ta: 'காலி' },
  'Galle Regional Office': {
    si: 'ගාල්ල ප්‍රාදේශීය කාර්යාලය',
    ta: 'காலி பிராந்திய அலுவலகம்',
  },
  Remote: { si: 'දුරස්ථ', ta: 'தொலைவிலிருந்து' },
  // Company name stays Latin; only the "HQ" descriptor localises.
  'Ceylon Brightways (Pvt) Ltd — HQ': {
    si: 'Ceylon Brightways (Pvt) Ltd — ප්‍රධාන කාර්යාලය',
    ta: 'Ceylon Brightways (Pvt) Ltd — தலைமையகம்',
  },

  // ── Organisation documents ────────────────────────────────────────────────
  'Employee Handbook v3': { si: 'සේවක අත්පොත v3', ta: 'ஊழியர் கையேடு v3' },
  'Expense Policy': { si: 'වියදම් ප්‍රතිපත්තිය', ta: 'செலவுக் கொள்கை' },
  'ISO 27001 Certificate': {
    si: 'ISO 27001 සහතිකය',
    ta: 'ISO 27001 சான்றிதழ்',
  },
  'Offer Letter Template': {
    si: 'රැකියා ලිපි අච්චුව',
    ta: 'பணி வழங்கல் கடிதத் தோரணி',
  },

  // ── Employee documents ────────────────────────────────────────────────────
  'Employment contract': { si: 'සේවා ගිවිසුම', ta: 'வேலை ஒப்பந்தம்' },
  'NIC copy': { si: 'ජා.හැ.ප. පිටපත', ta: 'தே.அ.அ. நகல்' },
  'Company VAT certificate': {
    si: 'සමාගම් VAT සහතිකය',
    ta: 'நிறுவன VAT சான்றிதழ்',
  },
  'Contract agreement': {
    si: 'කොන්ත්‍රාත් ගිවිසුම',
    ta: 'ஒப்பந்த உடன்படிக்கை',
  },
  'Work permit': { si: 'වැඩ බලපත්‍රය', ta: 'பணி அனுமதிப் பத்திரம்' },

  // ── Salary structures (pay bands) ─────────────────────────────────────────
  'Engineering Band 2': { si: 'ඉංජිනේරු කාණ්ඩ 2', ta: 'பொறியியல் நிலை 2' },
  'Finance Band 1': { si: 'මූල්‍ය කාණ්ඩ 1', ta: 'நிதி நிலை 1' },
  'Finance Band 3': { si: 'මූල්‍ය කාණ්ඩ 3', ta: 'நிதி நிலை 3' },
  'HR Band 2': { si: 'මානව සම්පත් කාණ්ඩ 2', ta: 'மனித வள நிலை 2' },
  'Ops Band 1': { si: 'මෙහෙයුම් කාණ්ඩ 1', ta: 'செயற்பாடு நிலை 1' },
  'Ops Band 3': { si: 'මෙහෙයුම් කාණ්ඩ 3', ta: 'செயற்பாடு நிலை 3' },
  'Sales Band 1': { si: 'විකුණුම් කාණ්ඩ 1', ta: 'விற்பனை நிலை 1' },
};

/**
 * Render a demo-fixture string in the active locale.
 *
 * Unknown values (person names, bank names, anything not a fixture label) are
 * returned unchanged, so this is safe to apply to any data-derived string.
 *
 * @param value - the English fixture value from `mock_data.ts`
 * @param locale - the active locale (`en-001` / `si-LK` / `ta-LK`)
 */
export function localise_fixture(value: string, locale: string): string {
  // `Object.hasOwn` rather than an `undefined` check: the index signature types
  // every lookup as present, so a truthiness test would be a dead branch.
  if (!Object.hasOwn(MOCK_FIXTURE_LABELS, value)) return value;
  const entry = MOCK_FIXTURE_LABELS[value];
  if (locale.startsWith('si')) return entry.si;
  if (locale.startsWith('ta')) return entry.ta;
  return value;
}

/**
 * Localise a `"Name, Job title"` composite.
 *
 * Manager fields and org-chart leaves store the person and their title in one
 * string. Only the title is translatable — the name is a proper noun — so this
 * splits at the final comma and localises the tail. Keying each composite whole
 * would need one entry per person-title pairing; this needs only the titles,
 * which are already in the table.
 *
 * A value with no comma, or whose title has no entry, is returned unchanged.
 */
export function localise_person_title(value: string, locale: string): string {
  const at = value.lastIndexOf(', ');
  if (at < 0) return localise_fixture(value, locale);
  const name = value.slice(0, at);
  const title = value.slice(at + 2);
  const localised = localise_fixture(title, locale);
  return localised === title ? value : `${name}, ${localised}`;
}
